import {
  DisconnectReason,
  LocalAudioTrack,
  type LocalTrackPublication,
  type Participant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  ScreenSharePresets,
  Track,
  type VideoTrack,
} from 'livekit-client';
import { TRACK, type Credentials } from '@elixir/shared';
import { api, ApiError } from '../state/api';
import { Store } from '../state/store';
import { AudioMixer } from './AudioMixer';
import { ActivityDetector, type Bus, targetGains } from './mixing';
import { AudioGate, type LiveRoute, MicRouter, type RouteName } from './routing';
import { type AudioTuning, loadTuning, saveTuning } from './tuning';

export type Phase = 'idle' | 'starting' | 'connected' | 'reconnecting' | 'ended';

export interface VoiceState {
  phase: Phase;
  /** Plain-language problem to show the user, if any. */
  problem: string | null;
  micMuted: boolean;
  whisperHeld: boolean;
  whisperLocked: boolean;
  announceHeld: boolean;
  announceLocked: boolean;
  /** Where your voice is going right now. */
  route: LiveRoute;
  teamRoom: 'none' | 'connecting' | 'connected';
  /** Identities speaking in the main room / whispering in your team room. */
  publicSpeakers: string[];
  teamSpeakers: string[];
  /** Your team is whispering, so public audio is faded down. */
  teamDucked: boolean;
  announcementMix: boolean;
  micLevelDb: number;
  screen: { track: VideoTrack; isLocal: boolean } | null;
  sharingAudio: boolean;
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  inputId: string;
  outputId: string;
  canPickOutput: boolean;
  tuning: AudioTuning;
}

interface RouteTrack {
  local: LocalAudioTrack;
  gate: AudioGate;
  pub?: LocalTrackPublication;
}

const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

const ROOM_OPTIONS = {
  adaptiveStream: false,
  dynacast: false,
  stopLocalTrackOnUnpublish: false,
  disconnectOnPageLeave: true,
};

const roleOf = (p: Participant): string => {
  try {
    return JSON.parse(p.metadata || '{}').role ?? 'player';
  } catch {
    return 'player';
  }
};

const isNotAllowed = (e: unknown) => (e as Error)?.name === 'NotAllowedError';

/**
 * One participant's voice connection.
 *
 * Players are connected to two LiveKit rooms at once: the main room (everyone) and their team room
 * (teammates only — the server never issues anyone else a token for it). Whispering just changes
 * which room's track carries their microphone; nobody switches calls.
 */
export class VoiceSession {
  readonly store: Store<VoiceState>;
  private mixer: AudioMixer;
  private router = new MicRouter();
  private teamActivity: ActivityDetector;
  private announceActivity: ActivityDetector;

  private raw: MediaStreamTrack | null = null;
  private routes = new Map<RouteName, RouteTrack>();
  private main: Room | null = null;
  private team: Room | null = null;
  private teamId: string | null = null;
  private desiredTeamId: string | null = null;
  private teamWork: Promise<void> = Promise.resolve();
  private teamRetry: number | null = null;
  private mainRetries = 0;
  private stopped = false;
  private announcingFlag = false;
  private lastMix = { teamWhisperActive: false, announcementActive: false };
  private lastLevelUpdate = 0;
  private ticker: number | null = null;

  /** Diagnostics mode (add ?debug to the URL): per-source levels via window.__elixirVoice.debugSnapshot(). */
  readonly debug = typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');
  private sourceInfo = new Map<string, { bus: Bus; identity: string }>();
  private levels = new Map<string, number>();

  /** Fires when whisper/announce mode changes, so the state channel can tell everyone. */
  onWhisperMode?: (active: boolean) => void;
  onAnnounceMode?: (active: boolean) => void;

  constructor(
    private creds: Credentials,
    private isHost: boolean,
    initial: { micMuted?: boolean; inputId?: string; outputId?: string } = {},
  ) {
    const tuning = loadTuning();
    this.store = new Store<VoiceState>({
      phase: 'idle',
      problem: null,
      micMuted: initial.micMuted ?? false,
      whisperHeld: false,
      whisperLocked: false,
      announceHeld: false,
      announceLocked: false,
      route: 'none',
      teamRoom: 'none',
      publicSpeakers: [],
      teamSpeakers: [],
      teamDucked: false,
      announcementMix: false,
      micLevelDb: -120,
      screen: null,
      sharingAudio: false,
      inputs: [],
      outputs: [],
      inputId: initial.inputId ?? 'default',
      outputId: initial.outputId ?? 'default',
      canPickOutput: false,
      tuning,
    });
    const getTuning = () => this.store.get().tuning;
    this.teamActivity = new ActivityDetector(getTuning);
    this.announceActivity = new ActivityDetector(getTuning);
    this.mixer = new AudioMixer(getTuning);
    this.mixer.onLevel = (id, db) => this.handleLevel(id, db);
    this.router.onChange = (route) => this.store.set({ route });
    if (this.debug) {
      this.mixer.meterAll = true;
      (window as unknown as { __elixirVoice: VoiceSession }).__elixirVoice = this;
    }
  }

  /** Diagnostics: what this browser is receiving right now, and how loud. */
  debugSnapshot() {
    const sources = [...this.sourceInfo.entries()].map(([id, info]) => ({
      id,
      ...info,
      db: Math.round(this.levels.get(id) ?? -120),
    }));
    return {
      route: this.router.route,
      teamId: this.teamId,
      gains: this.mixer.busGains(),
      teamDucked: this.store.get().teamDucked,
      announcementMix: this.store.get().announcementMix,
      ownMicDb: this.store.get().micLevelDb,
      outgoing: Object.fromEntries(
        [...this.routes.entries()].map(([name, r]) => [
          name,
          { gateOpen: r.gate.open, enabled: r.gate.track.enabled, lkMuted: r.local.isMuted, sameTrack: r.local.mediaStreamTrack === r.gate.track },
        ]),
      ),
      sources,
    };
  }

  // ------------------------------------------------------------------ lifecycle

  /** Call from a click handler: browsers only allow audio playback after a user gesture. */
  async start() {
    if (this.store.get().phase !== 'idle') return;
    this.store.set({ phase: 'starting', problem: null });
    try {
      // Never let a slow or blocked audio start hang the join.
      await Promise.race([this.mixer.resume(), new Promise((r) => setTimeout(r, 4000))]).catch(() => {});
      if (!this.mixer.running) this.waitForAudioUnlock();
      this.store.set({ canPickOutput: this.mixer.supportsOutputSelection });
      const out = this.store.get().outputId;
      if (out !== 'default') await this.mixer.setOutputDevice(out).catch(() => {});

      await this.captureMic(this.store.get().inputId);
      this.router.update({ isHost: this.isHost, micMuted: this.store.get().micMuted });
      await this.connectMainWithRetry();
      this.mainRetries = 0;
      this.store.set({ phase: 'connected' });
      await this.refreshDevices();
      navigator.mediaDevices.addEventListener('devicechange', this.refreshDevices);
      // Backup tick so fades still release if no level reports arrive.
      this.ticker = window.setInterval(() => this.refreshMix(), 250);
      // Join the team room if quiz state already told us the team.
      this.syncTeam(this.desiredTeamId);
    } catch (err) {
      this.store.set({ phase: 'idle', problem: this.describeStartError(err) });
      await this.teardownRooms();
      for (const r of this.routes.values()) r.gate.track.stop();
      this.routes.clear();
      this.raw?.stop();
      this.raw = null;
      throw err;
    }
  }

  async stop() {
    this.stopped = true;
    if (this.ticker) clearInterval(this.ticker);
    if (this.teamRetry) clearTimeout(this.teamRetry);
    navigator.mediaDevices?.removeEventListener('devicechange', this.refreshDevices);
    await this.teardownRooms();
    for (const r of this.routes.values()) r.gate.track.stop();
    this.routes.clear();
    this.raw?.stop();
    this.raw = null;
    await this.mixer.close();
    this.store.set({ phase: 'ended' });
  }

  // ------------------------------------------------------------------ controls

  setMicMuted(muted: boolean) {
    this.store.set({ micMuted: muted });
    this.router.update({ micMuted: muted });
    // Also signal LiveKit so others see the muted icon. Audio is already stopped by the gates.
    for (const r of this.routes.values()) void (muted ? r.local.mute() : r.local.unmute()).catch(() => {});
  }

  toggleMute() {
    this.setMicMuted(!this.store.get().micMuted);
  }

  setWhisperHeld(held: boolean) {
    if (this.isHost) return;
    this.store.set({ whisperHeld: held });
    this.applyWhisper();
  }

  toggleWhisperLock() {
    if (this.isHost) return;
    this.store.set((s) => ({ whisperLocked: !s.whisperLocked }));
    this.applyWhisper();
  }

  setAnnounceHeld(held: boolean) {
    if (!this.isHost) return;
    this.store.set({ announceHeld: held });
    this.applyAnnounce();
  }

  toggleAnnounceLock() {
    if (!this.isHost) return;
    this.store.set((s) => ({ announceLocked: !s.announceLocked }));
    this.applyAnnounce();
  }

  /** The quiz state says the quizmaster is announcing. */
  setAnnouncingFlag(active: boolean) {
    this.announcingFlag = active;
    this.refreshMix();
  }

  /** The quiz state says which team we're on. Joins/leaves the right team room. */
  syncTeam(teamId: string | null) {
    if (this.isHost) return;
    this.desiredTeamId = teamId;
    if (this.store.get().phase === 'idle' || this.stopped) return;
    this.teamWork = this.teamWork.then(() => this.applyTeam()).catch((err) => console.warn('[voice] team sync', err));
  }

  async setInputDevice(deviceId: string) {
    this.store.set({ inputId: deviceId });
    if (!this.raw) return;
    const old = this.raw;
    const oldRouteTracks = [...this.routes.values()].map((r) => r.gate.track);
    await this.captureMic(deviceId, true);
    for (const [name, r] of this.routes) {
      const clone = this.raw!.clone();
      const gate = new AudioGate(clone);
      await r.local.replaceTrack(clone, true);
      r.gate = gate;
      this.router.setGate(name, gate);
      if (name === 'team') void this.mixer.meterOnly('local:team', clone);
    }
    oldRouteTracks.forEach((t) => t.stop());
    old.stop();
  }

  async setOutputDevice(deviceId: string) {
    this.store.set({ outputId: deviceId });
    await this.mixer.setOutputDevice(deviceId).catch((err) => {
      this.store.set({ problem: `Could not switch speakers: ${(err as Error).message}` });
    });
  }

  updateTuning(patch: Partial<AudioTuning>) {
    const tuning = { ...this.store.get().tuning, ...patch };
    saveTuning(tuning);
    this.store.set({ tuning });
    this.refreshMix(performance.now(), true);
  }

  async startScreenShare() {
    if (!this.isHost || !this.main) return;
    try {
      await this.main.localParticipant.setScreenShareEnabled(
        true,
        {
          audio: true,
          systemAudio: 'include',
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
          contentHint: 'detail',
          resolution: ScreenSharePresets.h1080fps30.resolution,
        },
        { screenShareEncoding: ScreenSharePresets.h1080fps30.encoding },
      );
      this.updateLocalScreen();
    } catch (err) {
      if (!isNotAllowed(err)) this.store.set({ problem: `Screen sharing failed: ${(err as Error).message}` });
    }
  }

  async stopScreenShare() {
    await this.main?.localParticipant.setScreenShareEnabled(false);
    this.updateLocalScreen();
  }

  /** Browser blocked sound until the next click/keypress: ask for one, then resume. */
  private waitForAudioUnlock() {
    const message = 'Sound is paused by your browser. Click anywhere on the page to hear the quiz.';
    this.store.set({ problem: message });
    const unlock = () => {
      void this.mixer.resume().then(() => {
        if (!this.mixer.running) return;
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
        if (this.store.get().problem === message) this.store.set({ problem: null });
      });
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  clearProblem() {
    this.store.set({ problem: null });
  }

  // ------------------------------------------------------------------ mic capture

  private async captureMic(deviceId: string, swapOnly = false) {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('insecure');
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { ...MIC_CONSTRAINTS, ...(deviceId !== 'default' ? { deviceId: { exact: deviceId } } : {}) },
    });
    this.raw = stream.getAudioTracks()[0]!;
    this.raw.addEventListener('ended', () => {
      if (!this.stopped) this.store.set({ problem: 'Your microphone disconnected. Pick another one in Audio settings.' });
    });
    void this.mixer.meterOnly('local:mic', this.raw);
    if (!swapOnly) this.store.set({ micLevelDb: -120 });
  }

  /** A new gated clone of the mic for one route, published as its own track. */
  private async publishRoute(room: Room, name: RouteName) {
    const clone = this.raw!.clone();
    const gate = new AudioGate(clone);
    const local = new LocalAudioTrack(clone, MIC_CONSTRAINTS, true);
    if (this.store.get().micMuted) await local.mute();
    const trackName = name === 'public' ? TRACK.publicMic : name === 'team' ? TRACK.teamMic : TRACK.announce;
    const pub = await room.localParticipant.publishTrack(local, {
      name: trackName,
      source: Track.Source.Microphone,
      dtx: true,
      red: true,
    });
    const entry: RouteTrack = { local, gate, pub };
    this.routes.set(name, entry);
    this.router.setGate(name, gate);
    return entry;
  }

  private unpublishRoute(name: RouteName) {
    const r = this.routes.get(name);
    if (!r) return;
    this.router.setGate(name, null);
    r.gate.track.stop();
    this.routes.delete(name);
  }

  // ------------------------------------------------------------------ main room

  private async connectMain() {
    const tokens = await api.tokens(this.creds);
    const room = new Room(ROOM_OPTIONS);
    this.main = room;
    this.wireRoom(room, 'main');
    await room.connect(tokens.livekitUrl, tokens.main.token, { autoSubscribe: true });
    await this.publishRoute(room, 'public');
    if (this.isHost) await this.publishRoute(room, 'announce');
  }

  /** Network blips during the first connection are common; retry quietly before showing an error. */
  private async connectMainWithRetry(attempts = 3) {
    for (let i = 1; ; i++) {
      try {
        return await this.connectMain();
      } catch (err) {
        if (err instanceof ApiError || i >= attempts || this.stopped) throw err;
        console.warn(`[voice] connect attempt ${i} failed, retrying`, err);
        const failed = this.main;
        this.main = null;
        this.unpublishRoute('public');
        this.unpublishRoute('announce');
        await failed?.disconnect().catch(() => {});
        await new Promise((r) => setTimeout(r, 700 * i));
      }
    }
  }

  private async reconnectMain() {
    if (this.stopped) return;
    this.store.set({ phase: 'reconnecting' });
    this.unpublishRoute('public');
    this.unpublishRoute('announce');
    const delay = Math.min(15_000, 1000 * 2 ** this.mainRetries++);
    await new Promise((r) => setTimeout(r, delay));
    if (this.stopped) return;
    try {
      await this.connectMain();
      this.mainRetries = 0;
      this.store.set({ phase: 'connected', problem: null });
      if (!this.isHost) {
        // The team room may have dropped too; make sure we're back in it.
        this.syncTeam(this.desiredTeamId);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        this.store.set({ phase: 'ended', problem: err.message });
        return;
      }
      const failed = this.main;
      this.main = null;
      await failed?.disconnect().catch(() => {});
      void this.reconnectMain();
    }
  }

  // ------------------------------------------------------------------ team room

  private async applyTeam() {
    if (this.stopped) return;
    const desired = this.desiredTeamId;
    if (desired === this.teamId && (desired === null || this.team)) return;

    await this.leaveTeam();
    if (desired === null) return;

    this.store.set({ teamRoom: 'connecting' });
    try {
      const tokens = await api.tokens(this.creds);
      if (!tokens.team || tokens.team.teamId !== this.desiredTeamId) {
        // The team changed again while we were fetching; the queued sync will handle it.
        this.store.set({ teamRoom: 'none' });
        if (this.desiredTeamId && !tokens.team) this.scheduleTeamRetry();
        return;
      }
      const room = new Room(ROOM_OPTIONS);
      this.team = room;
      this.teamId = tokens.team.teamId;
      this.wireRoom(room, 'team');
      await room.connect(tokens.livekitUrl, tokens.team.token, { autoSubscribe: true });
      const entry = await this.publishRoute(room, 'team');
      void this.mixer.meterOnly('local:team', entry.gate.track);
      this.store.set({ teamRoom: 'connected' });
    } catch (err) {
      console.warn('[voice] team room failed', err);
      await this.leaveTeam();
      this.scheduleTeamRetry();
    }
  }

  private async leaveTeam() {
    // Drop whisper first so the router never falls back to anything unexpected.
    this.store.set({ whisperHeld: false, whisperLocked: false });
    this.applyWhisper();
    this.unpublishRoute('team');
    this.mixer.remove('local:team');
    this.teamActivity.reset();
    const room = this.team;
    this.team = null;
    this.teamId = null;
    this.store.set({ teamRoom: 'none', teamSpeakers: [] });
    if (room) await room.disconnect().catch(() => {});
  }

  private scheduleTeamRetry() {
    if (this.teamRetry || this.stopped) return;
    this.teamRetry = window.setTimeout(() => {
      this.teamRetry = null;
      this.syncTeam(this.desiredTeamId);
    }, 2000);
  }

  // ------------------------------------------------------------------ room events

  private wireRoom(room: Room, kind: 'main' | 'team') {
    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: Participant) => {
        if (room !== this.main && room !== this.team) return;
        if (track.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare) {
          this.store.set({ screen: { track: track as VideoTrack, isLocal: false } });
          return;
        }
        if (track.kind !== Track.Kind.Audio) return;
        const bus = this.busFor(kind, pub, p);
        const id = `${bus}:${pub.trackSid}`;
        this.sourceInfo.set(id, { bus, identity: p.identity });
        void this.mixer.play(id, track.mediaStreamTrack, bus, { meter: bus === 'team' || bus === 'announce' });
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, p: Participant) => {
        if (track.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare) {
          if (this.store.get().screen?.track === track) this.store.set({ screen: null });
          return;
        }
        const bus = this.busFor(kind, pub, p);
        const id = `${bus}:${pub.trackSid}`;
        this.mixer.remove(id);
        this.sourceInfo.delete(id);
        this.levels.delete(id);
        this.teamActivity.forget(id);
        this.announceActivity.forget(id);
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        const ids = speakers.map((s) => s.identity);
        this.store.set(kind === 'main' ? { publicSpeakers: ids } : { teamSpeakers: ids });
      })
      .on(RoomEvent.Reconnecting, () => {
        if (kind === 'main') this.store.set({ phase: 'reconnecting' });
      })
      .on(RoomEvent.Reconnected, () => {
        if (kind === 'main') this.store.set({ phase: 'connected' });
      })
      .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
        if (pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio) this.updateLocalScreen();
      })
      .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        if (this.stopped) return;
        if (kind === 'team') {
          if (room !== this.team) return; // we left on purpose
          this.team = null;
          this.teamId = null;
          this.unpublishRoute('team');
          this.store.set({ teamRoom: 'none', teamSpeakers: [] });
          // Removed by the server = moved to another team; the state channel brings the new team.
          this.scheduleTeamRetry();
          return;
        }
        if (room !== this.main) return;
        // During the first join, failures are handled by connectMainWithRetry.
        if (this.store.get().phase === 'starting') return;
        this.main = null;
        this.store.set({ publicSpeakers: [], screen: null });
        if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
          this.store.set({ phase: 'ended', problem: 'You opened this quiz in another tab or device, so this one was disconnected.' });
          void this.stop();
          return;
        }
        if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
          this.store.set({ phase: 'ended', problem: 'The quizmaster removed you from this quiz.' });
          void this.stop();
          return;
        }
        void this.reconnectMain();
      });
  }

  private busFor(kind: 'main' | 'team', pub: RemoteTrackPublication, p: Participant): Bus {
    if (kind === 'team') return 'team';
    if (pub.source === Track.Source.ScreenShareAudio) return 'media';
    if (roleOf(p) === 'host') return pub.trackName === TRACK.announce ? 'announce' : 'qm';
    return 'public';
  }

  private updateLocalScreen() {
    const lp = this.main?.localParticipant;
    const video = lp?.getTrackPublication(Track.Source.ScreenShare)?.track as VideoTrack | undefined;
    const audio = lp?.getTrackPublication(Track.Source.ScreenShareAudio)?.track;
    this.store.set({ screen: video ? { track: video, isLocal: true } : null, sharingAudio: Boolean(audio) });
  }

  // ------------------------------------------------------------------ mixing

  private wasWhispering = false;
  private applyWhisper() {
    const s = this.store.get();
    const active = s.whisperHeld || s.whisperLocked;
    this.router.update({ whisper: active });
    if (active !== this.wasWhispering) {
      this.wasWhispering = active;
      this.onWhisperMode?.(active);
    }
  }

  private wasAnnouncing = false;
  private applyAnnounce() {
    const s = this.store.get();
    const active = s.announceHeld || s.announceLocked;
    this.router.update({ announce: active });
    if (active !== this.wasAnnouncing) {
      this.wasAnnouncing = active;
      this.onAnnounceMode?.(active);
      this.announcingFlag = active;
      this.refreshMix();
    }
  }

  private handleLevel(id: string, db: number) {
    const now = performance.now();
    if (this.debug) this.levels.set(id, db);
    if (id === 'local:mic') {
      if (now - this.lastLevelUpdate > 80) {
        this.lastLevelUpdate = now;
        this.store.set({ micLevelDb: Math.round(db) });
      }
      return;
    }
    if (id === 'local:team' || id.startsWith('team:')) this.teamActivity.observe(id, db, now);
    else if (id.startsWith('announce:')) this.announceActivity.observe(id, db, now);
    else return; // other sources are only metered in diagnostics mode
    this.refreshMix(now);
  }

  private refreshMix(now = performance.now(), force = false) {
    const inputs = {
      teamWhisperActive: !this.isHost && this.teamActivity.isActive(now),
      announcementActive: this.announcingFlag || this.announceActivity.isActive(now),
    };
    if (
      !force &&
      inputs.teamWhisperActive === this.lastMix.teamWhisperActive &&
      inputs.announcementActive === this.lastMix.announcementActive
    ) {
      return;
    }
    this.lastMix = inputs;
    this.mixer.setTargets(targetGains(inputs, this.store.get().tuning));
    this.store.set({ teamDucked: inputs.teamWhisperActive, announcementMix: inputs.announcementActive });
  }

  // ------------------------------------------------------------------ devices & errors

  private refreshDevices = async () => {
    try {
      const [inputs, outputs] = await Promise.all([
        Room.getLocalDevices('audioinput', false),
        Room.getLocalDevices('audiooutput', false),
      ]);
      this.store.set({ inputs, outputs });
    } catch {
      /* device list is optional */
    }
  };

  private async teardownRooms() {
    const main = this.main;
    this.main = null;
    const team = this.team;
    this.team = null;
    await Promise.all([main?.disconnect().catch(() => {}), team?.disconnect().catch(() => {})]);
  }

  private describeStartError(err: unknown): string {
    const e = err as Error;
    if (e?.message === 'insecure') return 'Microphones only work on a secure (https) page. Ask the host for the https link.';
    if (e?.name === 'NotAllowedError') {
      return 'Microphone access is blocked. Click the lock icon in the address bar, allow the microphone, then try again.';
    }
    if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
      return 'No microphone found. Plug one in or pick another in Audio settings, then try again.';
    }
    if (e?.name === 'NotReadableError') return 'Your microphone is being used by another app. Close it and try again.';
    if (err instanceof ApiError) return err.message;
    return `Could not connect to the voice room (${e?.message ?? 'unknown error'}). Try again.`;
  }
}
